import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { Search, CheckCircle, Clock } from 'lucide-react';
import { deviceBrands } from '../data/mock';

export const SellDevice = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    deviceType: '',
    brand: '',
    model: '',
    condition: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const apiUrl = window.location.origin;
      
      const response = await fetch(`${apiUrl}/api/diagnostic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          deviceType: formData.deviceType,
          brand: formData.brand,
          model: formData.model,
          problem: formData.condition
        }),
      });

      if (response.ok) {
        setShowSuccess(true);
        toast({
          title: "✅ Demande de diagnostic envoyée !",
          description: "Nous vous contacterons rapidement. Un email de confirmation vous a été envoyé.",
          duration: 6000,
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          deviceType: '',
          brand: '',
          model: '',
          condition: ''
        });
      } else {
        toast({
          title: "❌ Erreur",
          description: "Une erreur est survenue. Veuillez réessayer.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: "❌ Erreur",
        description: "Impossible d'envoyer la demande. Vérifiez votre connexion.",
        variant: "destructive"
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getBrandsForDeviceType = () => {
    switch (formData.deviceType) {
      case 'smartphone':
        return deviceBrands.smartphones;
      case 'tablet':
        return deviceBrands.tablets;
      case 'computer':
        return deviceBrands.computers;
      default:
        return [];
    }
  };

  return (
    <section id="sell" className="py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Message de succès */}
        {showSuccess && (
          <div className="mb-8 bg-green-50 border-2 border-green-500 rounded-2xl p-6 shadow-xl animate-bounce-slow">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-green-800 mb-1">✅ Demande de diagnostic envoyée !</h3>
                <p className="text-green-700">
                  Nous vous contacterons rapidement. 
                  <strong> Vérifiez votre email</strong> pour la confirmation.
                </p>
              </div>
              <button
                onClick={() => setShowSuccess(false)}
                className="text-green-600 hover:text-green-800 font-bold text-2xl"
              >×</button>
            </div>
          </div>
        )}
        
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Info */}
          <div>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6">
              <Search className="text-blue-600" size={32} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Diagnostic Gratuit
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Votre appareil ne fonctionne plus correctement ? Profitez de notre service de diagnostic 100% gratuit pour identifier le problème !
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="text-blue-600" size={18} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Diagnostic complet et gratuit
                  </h3>
                  <p className="text-gray-600">
                    Analyse approfondie de votre appareil sans aucun frais
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Clock className="text-blue-600" size={18} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Résultat rapide en 15-30 minutes
                  </h3>
                  <p className="text-gray-600">
                    Diagnostic express pour connaître rapidement le problème
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="text-blue-600" size={18} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Devis transparent après diagnostic
                  </h3>
                  <p className="text-gray-600">
                    Vous décidez de réparer ou non, aucune obligation
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                💡 Service sans engagement
              </h3>
              <p className="text-gray-600 mb-4">
                Le diagnostic est offert même si vous ne réparez pas chez nous. Notre objectif : vous aider à prendre la meilleure décision !
              </p>
              <p className="text-sm text-gray-500 italic">
                ℹ️ Note : Nous ne rachetons plus d'appareils chez les particuliers. Nous nous concentrons sur la réparation et le diagnostic professionnel.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <Card className="border-2 shadow-2xl">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
                <CardTitle className="text-2xl">Demander un diagnostic gratuit</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="sell-name">Nom complet *</Label>
                    <Input
                      id="sell-name"
                      placeholder="Votre nom"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      className="border-2 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sell-email">Email *</Label>
                      <Input
                        id="sell-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        className="border-2 focus:border-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sell-phone">Téléphone *</Label>
                      <Input
                        id="sell-phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                        className="border-2 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sell-address">Adresse (optionnel)</Label>
                    <Input
                      id="sell-address"
                      placeholder="Votre adresse"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      className="border-2 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sell-deviceType">Type d'appareil *</Label>
                      <Select
                        value={formData.deviceType}
                        onValueChange={(value) => {
                          handleChange('deviceType', value);
                          handleChange('brand', '');
                        }}
                      >
                        <SelectTrigger className="border-2 focus:border-blue-500">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="smartphone">Smartphone</SelectItem>
                          <SelectItem value="tablet">Tablette</SelectItem>
                          <SelectItem value="computer">Ordinateur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sell-brand">Marque *</Label>
                      <Select
                        value={formData.brand}
                        onValueChange={(value) => handleChange('brand', value)}
                        disabled={!formData.deviceType}
                      >
                        <SelectTrigger className="border-2 focus:border-blue-500">
                          <SelectValue placeholder="Marque" />
                        </SelectTrigger>
                        <SelectContent>
                          {getBrandsForDeviceType().map((brand) => (
                            <SelectItem key={brand} value={brand.toLowerCase()}>
                              {brand}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sell-model">Modèle *</Label>
                    <Input
                      id="sell-model"
                      placeholder="Ex: iPhone 14, Galaxy S23..."
                      value={formData.model}
                      onChange={(e) => handleChange('model', e.target.value)}
                      required
                      className="border-2 focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sell-condition">Décrivez le problème *</Label>
                    <Textarea
                      id="sell-condition"
                      placeholder="Expliquez les symptômes : écran cassé, ne s'allume plus, problème de batterie..."
                      value={formData.condition}
                      onChange={(e) => handleChange('condition', e.target.value)}
                      required
                      rows={3}
                      className="border-2 focus:border-blue-500"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 text-lg font-semibold rounded-xl shadow-lg"
                  >
                    Demander mon diagnostic gratuit
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
